import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Activity,
    AlertTriangle,
    CheckCircle,
    Clock,
    TrendingUp,
    MessageSquare,
    Database,
} from 'lucide-react';
import { fetchTriageSummary } from '../data/adapters';
import { TriageSummary } from '../types';
import { nativeCommsAPI } from '../../../api/nativeComms';
import { CommsHealth } from '../../../types/nativeComms';
import { useEngineeringBridgeContext } from '../context/EngineeringBridgeContext';

export const SystemTriageTab: React.FC = () => {
    const { isDemo } = useEngineeringBridgeContext();
    const [data, setData] = useState<TriageSummary | null>(null);
    const [commsHealth, setCommsHealth] = useState<CommsHealth | null>(null);
    const [loading, setLoading] = useState(true);
    const [noData, setNoData] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                setNoData(false);
                const [triageData, commsData] = await Promise.all([
                    fetchTriageSummary(isDemo).catch(() => null),
                    nativeCommsAPI.getHealth().catch(() => null),
                ]);
                setData(triageData);
                setCommsHealth(commsData);
                if (!triageData) {
                    setNoData(true);
                }
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [isDemo]);

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'healthy':
                return 'text-green-400';
            case 'degraded':
                return 'text-yellow-400';
            case 'down':
                return 'text-red-400';
            default:
                return 'text-slate-400';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'healthy':
                return <CheckCircle size={16} className="text-green-400" />;
            case 'degraded':
                return <AlertTriangle size={16} className="text-yellow-400" />;
            case 'down':
                return <AlertTriangle size={16} className="text-red-400" />;
            default:
                return <Clock size={16} className="text-slate-400" />;
        }
    };

    if (loading) {
        return (
            <div className="space-y-6">
                {/* Key Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-32 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
                {/* Service Status Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                    <div
                        className="h-64 bg-slate-800/50 rounded-lg animate-pulse"
                        data-testid="skeleton-placeholder"
                    />
                </div>
            </div>
        );
    }

    const renderNoDataPlaceholder = (title: string) => (
        <div className="flex flex-col items-center justify-center py-8 text-slate-500">
            <Database size={32} className="mb-2 opacity-50" />
            <p className="text-sm">{title}</p>
            <p className="text-xs mt-1">No data available</p>
        </div>
    );

    if (noData || !data) {
        return (
            <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Overall Health</h3>
                            <Activity size={20} className="text-blue-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--%</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">MTTR (7d)</h3>
                            <Clock size={20} className="text-green-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--h</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Active Queues</h3>
                            <TrendingUp size={20} className="text-purple-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                        className="evolut-metric-card p-6"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-sm font-medium text-slate-400">Services</h3>
                            <CheckCircle size={20} className="text-cyan-400" />
                        </div>
                        <div className="text-3xl font-bold text-slate-600 mb-2">--</div>
                        <p className="text-sm text-slate-500">No data available</p>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">Service Status</h3>
                    {renderNoDataPlaceholder('Service status')}
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.7 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">
                            Queue Depth & Wait Times
                        </h3>
                        {renderNoDataPlaceholder('Queue metrics')}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 }}
                        className="evolut-metric-card p-6"
                    >
                        <h3 className="text-lg font-semibold text-white mb-6">
                            Latency Metrics (P95/P99)
                        </h3>
                        {renderNoDataPlaceholder('Latency metrics')}
                    </motion.div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Key Metrics Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {/* Overall Health Score */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Overall Health</h3>
                        <Activity size={20} className="text-blue-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">
                        {Math.round(
                            (data.services.filter(s => s.status === 'healthy').length /
                                data.services.length) *
                                100
                        )}
                        %
                    </div>
                    <p className="text-sm text-slate-400">
                        {data.services.filter(s => s.status === 'healthy').length} of{' '}
                        {data.services.length} services healthy
                    </p>
                </motion.div>

                {/* MTTR */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">MTTR (7d)</h3>
                        <Clock size={20} className="text-green-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">{data.mttrHours7d}h</div>
                    <p className="text-sm text-slate-400">Mean time to resolution</p>
                </motion.div>

                {/* Total Queues */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Active Queues</h3>
                        <TrendingUp size={20} className="text-purple-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">{data.queues.length}</div>
                    <p className="text-sm text-slate-400">Message queues monitored</p>
                </motion.div>

                {/* Total Services */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-slate-400">Services</h3>
                        <CheckCircle size={20} className="text-cyan-400" />
                    </div>
                    <div className="text-3xl font-bold text-white mb-2">{data.services.length}</div>
                    <p className="text-sm text-slate-400">Infrastructure services</p>
                </motion.div>
            </div>

            {/* Service Status Grid */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="evolut-metric-card p-6"
            >
                <h3 className="text-lg font-semibold text-white mb-6">Service Status</h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {data.services.map((service, index) => (
                        <motion.div
                            key={service.name}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * index }}
                            className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50"
                        >
                            <div className="flex items-center space-x-3">
                                {getStatusIcon(service.status)}
                                <div>
                                    <h4 className="font-medium text-white">{service.name}</h4>
                                    <p
                                        className={`text-sm ${getStatusColor(service.status)} capitalize`}
                                    >
                                        {service.status}
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-sm text-slate-400">Error Rate</div>
                                <div className="text-lg font-semibold text-white">
                                    {(service.errorRate * 100).toFixed(2)}%
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Communications KPIs */}
            {commsHealth && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="evolut-metric-card p-6"
                >
                    <div className="flex items-center space-x-3 mb-6">
                        <MessageSquare size={24} className="text-blue-400" />
                        <h3 className="text-lg font-semibold text-white">
                            Native Communications Health
                        </h3>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                            <div className="text-2xl font-bold text-green-400">
                                {commsHealth.deliveredPct}%
                            </div>
                            <div className="text-sm text-slate-400">Delivered</div>
                        </div>
                        <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                            <div className="text-2xl font-bold text-blue-400">
                                {commsHealth.sendSuccessPct}%
                            </div>
                            <div className="text-sm text-slate-400">Send Success</div>
                        </div>
                        <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                            <div className="text-2xl font-bold text-yellow-400">
                                {commsHealth.queue.depth}
                            </div>
                            <div className="text-sm text-slate-400">Queue Depth</div>
                        </div>
                        <div className="text-center p-3 bg-slate-800/50 rounded-lg">
                            <div className="text-2xl font-bold text-purple-400">
                                ${commsHealth.costPerMessageUsd}
                            </div>
                            <div className="text-sm text-slate-400">Cost/Message</div>
                        </div>
                    </div>
                </motion.div>
            )}

            {/* Queue Depth & Latency */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Queue Depth */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">
                        Queue Depth & Wait Times
                    </h3>
                    <div className="space-y-4">
                        {data.queues.map(queue => (
                            <div
                                key={queue.name}
                                className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                            >
                                <div>
                                    <h4 className="font-medium text-white">{queue.name}</h4>
                                    <p className="text-sm text-slate-400">
                                        P95 Wait: {queue.p95WaitSec}s
                                    </p>
                                </div>
                                <div className="text-right">
                                    <div className="text-2xl font-bold text-white">
                                        {queue.depth}
                                    </div>
                                    <div className="text-sm text-slate-400">messages</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Latency Metrics */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                    className="evolut-metric-card p-6"
                >
                    <h3 className="text-lg font-semibold text-white mb-6">
                        Latency Metrics (P95/P99)
                    </h3>
                    <div className="space-y-4">
                        {data.services.map(service => (
                            <div
                                key={service.name}
                                className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                            >
                                <div>
                                    <h4 className="font-medium text-white">{service.name}</h4>
                                    <p className="text-sm text-slate-400">Response times</p>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-semibold text-white">
                                        {service.p95ms}ms / {service.p99ms}ms
                                    </div>
                                    <div className="text-sm text-slate-400">P95 / P99</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </div>
    );
};
